/**
 * AlertCard — Pressable alert banner with icon, title, description, and chevron
 *
 * Used for: pending offers alert, low stock warning, action-required notices
 * From: MerchantDashboardScreen alertCard, InventoryReportsScreen alertCard
 */
import type { ReactNode } from 'react'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'
import { IconBadge } from '../elements/IconBadge'

const AlertCardFrame = styled(XStack, {
  name: 'AlertCard',
  role: 'button',
  cursor: 'pointer',
  userSelect: 'none',
  ai: 'center',
  gap: '$sm',
  bg: '$backgroundCard',
  br: '$lg',
  p: '$md',
  pressStyle: { opacity: 0.8, scale: 0.99 },
  hoverStyle: { opacity: 0.9 },

  variants: {
    severity: {
      warning: { bg: 'rgba(245, 158, 11, 0.08)' },
      error: { bg: 'rgba(239, 68, 68, 0.08)' },
      info: { bg: 'rgba(59, 130, 246, 0.08)' },
      neutral: { bg: '$backgroundCard' },
    },
  } as const,

  defaultVariants: {
    severity: 'neutral',
  },
})

const AlertContent = styled(YStack, {
  name: 'AlertContent',
  f: 1,
  gap: 2,
})

const AlertTitle = styled(TamaguiText, {
  name: 'AlertTitle',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '600',
  color: '$color',
})

const AlertDescription = styled(TamaguiText, {
  name: 'AlertDescription',
  fontFamily: '$body',
  fontSize: 12,
  color: '$colorSecondary',
})

type AlertCardFrameProps = GetProps<typeof AlertCardFrame>

interface AlertCardProps extends Omit<AlertCardFrameProps, 'severity' | 'right'> {
  icon: ReactNode
  title: string
  description?: string
  right?: ReactNode
  severity?: 'warning' | 'error' | 'info' | 'neutral'
}

export const AlertCard = AlertCardFrame.styleable<AlertCardProps>(
  ({ icon, title, description, right, severity = 'neutral', onPress, ...props }, ref) => {
    return (
      <AlertCardFrame ref={ref} severity={severity} onPress={onPress} {...props}>
        <IconBadge bg="$colorPrimaryMuted">{icon}</IconBadge>
        <AlertContent>
          <AlertTitle>{title}</AlertTitle>
          {description && <AlertDescription>{description}</AlertDescription>}
        </AlertContent>
        {right}
      </AlertCardFrame>
    )
  },
)
